import { urls } from '@repo/app-constants';
import { Kit } from '@repo/types';
import { Icon, Typography } from '@repo/ui';
import { AgeGroup, getAgeGroup } from '@repo/utils';
import resampleBg from 'assets/images/summary/resample_bg_2.png';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useCheckoutKit from 'hooks/useCheckoutKit';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import { useShowTinyPlusUpsell } from 'hooks/useShowTinyPlusUpsell';
import useFeature from 'services/features/useFeature';
import { type KeyboardEvent, type MouseEvent, useEffect } from 'react';
import useSubscriptions from 'services/general/subscriptionsList';
import styles from './RetestStep.module.css';

const RETEST_UTM =
  'utm_source=tinyhealth&utm_medium=webapp&utm_campaign=retest-step';
const UPSELL_UTM =
  'utm_source=tinyhealth&utm_medium=webapp&utm_campaign=retest-step-upsell';

const getKitAgeGroup = (kit?: Kit | null): AgeGroup =>
  kit?.age_at_sampling ? getAgeGroup(kit.age_at_sampling) : AgeGroup.ADULT;

// The retest step is an upsell moment: it is hidden unless the rollout flag is
// on (also while the flag or the portal config is loading), and practitioner
// and partner patients get no upsell push at all, so for them the step goes
// away too. The step itself (title, position) comes from Science; this only
// decides whether the plan shows it, and reports the impression when it does.
export const useShowRetestStep = ({
  kitId,
  hasRetestStep,
}: {
  kitId?: string | null;
  hasRetestStep: boolean;
}) => {
  const { currentKit } = useCurrentKitAndTinyAccount();
  const { trackEventOnce } = useAnalytics();
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs(currentKit);
  const { isAnyTinyPlus } = useSubscriptions();
  const { isFeatureEnabled: isUpsellMomentsEnabled } = useFeature(
    'tiny_plus_upsell_moments',
  );

  const showStep =
    hasRetestStep &&
    isUpsellMomentsEnabled &&
    !isPortalConfigLoading &&
    !portalConfig.hide_upsells;
  const ageGroup = getKitAgeGroup(currentKit);

  useEffect(() => {
    if (!showStep) return;
    trackEventOnce('Retest Step Viewed', `retest-${kitId}`, {
      age_group: ageGroup,
      is_tiny_plus: isAnyTinyPlus,
    });
  }, [kitId, showStep]);

  return showStep;
};

const RetestStep = () => {
  const { currentKit } = useCurrentKitAndTinyAccount();
  const { trackEvent } = useAnalytics();
  const showUpsellBanner = useShowTinyPlusUpsell(currentKit);
  const { checkoutUrl } = useCheckoutKit({
    kitType: currentKit?.sampling_type,
  });

  const ageGroup = getKitAgeGroup(currentKit);
  const orderUrl = `${checkoutUrl}&${RETEST_UTM}`;
  // TINY_PLUS_UPGRADE_OFFER already carries ?discount=, so the UTMs append with &.
  const upgradeUrl = `${urls.TINY_PLUS_UPGRADE_OFFER}&${UPSELL_UTM}`;

  const handleOrderKit = (e: MouseEvent | KeyboardEvent) => {
    if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return;
    trackEvent('Retest Upsell CTA Clicked', {
      cta: 'order_kit',
      age_group: ageGroup,
      href: orderUrl,
    });
    window.open(orderUrl, '_blank');
  };

  const handleUpgrade = () => {
    trackEvent('Retest Upsell CTA Clicked', {
      cta: 'upgrade_tiny_plus',
      age_group: ageGroup,
      href: upgradeUrl,
    });
    window.open(upgradeUrl, '_blank');
  };

  return (
    <div className={styles.content}>
      <div
        className={styles.orderKitCard}
        onClick={handleOrderKit}
        role='button'
        tabIndex={0}
        onKeyDown={handleOrderKit}
      >
        <div className={styles.orderKitLeft}>
          <Icon name='heartHealth' size='m' />
          <Typography variant='body-m'>Order your next kit</Typography>
        </div>
        <Icon name='chevronForward' size='s' />
      </div>
      {showUpsellBanner && (
        <div className={styles.upsellBanner}>
          <img
            src={resampleBg}
            alt='Tiny Health kit'
            className={styles.upsellImage}
          />
          <div className={styles.upsellText}>
            <Typography variant='heading-m' color='dark'>
              Upgrade to Tiny+ and save on retesting
            </Typography>
            <Typography variant='body-s' color='grey'>
              Tiny+ members save $80/kit.
            </Typography>
            <button onClick={handleUpgrade} className={styles.upgradeButton}>
              <Icon
                name='awardFilled'
                size='s'
                className={styles.upgradeIcon}
              />
              Upgrade to Tiny+
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetestStep;
